/**
 * tags-store — TanStack Query hooks pra tags (FEAT-V2-031).
 *
 * Endpoints consumidos:
 *   - GET    /api/v1/tags?workflow=<slug>
 *   - GET    /api/v1/tags/palette
 *   - POST   /api/v1/tags                (admin)
 *   - PUT    /api/v1/tags/:id            (admin)
 *   - DELETE /api/v1/tags/:id            (admin)
 *   - GET    /api/v1/cards/:type/:id/tags
 *   - POST   /api/v1/cards/:type/:id/tags
 *   - DELETE /api/v1/cards/:type/:id/tags/:tagId
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export interface Tag {
  id: string;
  nome: string;
  cor: string;
  icone: string | null;
  workflowsPermitidos: string[] | null;
  descricao: string | null;
  ativo: boolean;
  criadoEmIso: string;
  criadoPor: string | null;
  /** Só presente na resposta de GET /tags/:id */
  usageCount?: number;
}

export type CardType = 'deal' | 'service_order' | 'task';

export const TAG_KEYS = {
  catalog: (workflowSlug?: string | null, includeInactive?: boolean) =>
    ['v1', 'tags', 'catalog', workflowSlug ?? null, includeInactive ?? false] as const,
  palette: ['v1', 'tags', 'palette'] as const,
  detail: (id: string) => ['v1', 'tags', 'detail', id] as const,
  cardTags: (cardType: CardType, cardId: string) =>
    ['v1', 'cards', cardType, cardId, 'tags'] as const,
};

// ─── Catálogo (lista) ────────────────────────────────────────────────────

export function useTagsCatalog(
  workflowSlug?: string | null,
  opts?: { includeInactive?: boolean; enabled?: boolean },
) {
  const includeInactive = opts?.includeInactive ?? false;
  return useQuery({
    queryKey: TAG_KEYS.catalog(workflowSlug, includeInactive),
    queryFn: () => {
      const params = new URLSearchParams();
      if (workflowSlug) params.set('workflow', workflowSlug);
      if (includeInactive) params.set('includeInactive', '1');
      const qs = params.toString();
      return api.get<Tag[]>(`/api/v1/tags${qs ? `?${qs}` : ''}`);
    },
    enabled: hasToken() && (opts?.enabled ?? true),
    staleTime: 5 * 60_000, // 5min — admin altera raro
  });
}

export function useTagsPalette() {
  return useQuery({
    queryKey: TAG_KEYS.palette,
    queryFn: () => api.get<{ colors: string[] }>('/api/v1/tags/palette'),
    enabled: hasToken(),
    staleTime: Infinity, // paleta é constante
  });
}

export function useTagDetail(id: string | null) {
  return useQuery({
    queryKey: TAG_KEYS.detail(id ?? ''),
    queryFn: () => api.get<Tag>(`/api/v1/tags/${encodeURIComponent(id!)}`),
    enabled: hasToken() && !!id,
    staleTime: 60_000,
  });
}

// ─── Catálogo: mutations (admin) ─────────────────────────────────────────

export interface CreateTagPayload {
  nome: string;
  cor: string;
  icone?: string | null;
  workflowsPermitidos?: string[] | null;
  descricao?: string | null;
}

export type UpdateTagPayload = Partial<CreateTagPayload> & { ativo?: boolean };

export function useTagsCatalogActions() {
  const qc = useQueryClient();

  const invalidateAll = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['v1', 'tags'] });
  }, [qc]);

  const create = useMutation({
    mutationFn: (payload: CreateTagPayload) => api.post<Tag>('/api/v1/tags', payload),
    onSuccess: invalidateAll,
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTagPayload }) =>
      api.put<Tag>(`/api/v1/tags/${encodeURIComponent(id)}`, payload),
    onSuccess: invalidateAll,
  });

  const softDelete = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: true; softDeleted: true; previousUsageCount: number }>(
        `/api/v1/tags/${encodeURIComponent(id)}`,
      ),
    onSuccess: invalidateAll,
  });

  return { create, update, softDelete };
}

// ─── Card tags ───────────────────────────────────────────────────────────

export function useCardTags(cardType: CardType | null, cardId: string | null) {
  return useQuery({
    queryKey: TAG_KEYS.cardTags(cardType ?? 'deal', cardId ?? ''),
    queryFn: () =>
      api.get<Tag[]>(
        `/api/v1/cards/${encodeURIComponent(cardType!)}/${encodeURIComponent(cardId!)}/tags`,
      ),
    enabled: hasToken() && !!cardType && !!cardId,
    staleTime: 30_000,
  });
}

export function useCardTagActions(cardType: CardType, cardId: string) {
  const qc = useQueryClient();
  const invalidate = useCallback(
    () => qc.invalidateQueries({ queryKey: TAG_KEYS.cardTags(cardType, cardId) }),
    [qc, cardType, cardId],
  );

  // BUG-V2-058 (2026-05-25): tag muta cache do CARD também (DealCard renderiza
  // badges no header — sem invalidate dessas listagens, Kanban fica stale até
  // próximo refetch periódico).
  const invalidateCardLists = useCallback(() => {
    if (cardType === 'deal') {
      void qc.invalidateQueries({ queryKey: ['v1', 'deals'] });
    } else if (cardType === 'service_order') {
      void qc.invalidateQueries({ queryKey: ['v1', 'service-orders'] });
    }
  }, [qc, cardType]);

  const add = useMutation({
    mutationFn: (tagId: string) =>
      api.post<Tag & { added: boolean }>(
        `/api/v1/cards/${encodeURIComponent(cardType)}/${encodeURIComponent(cardId)}/tags`,
        { tagId },
      ),
    onSuccess: () => {
      void invalidate();
      // Catálogo conta usage count — invalida pra refletir +1.
      void qc.invalidateQueries({ queryKey: ['v1', 'tags'] });
      // BUG-V2-058: card list refletir badge no header.
      invalidateCardLists();
    },
  });

  const remove = useMutation({
    mutationFn: (tagId: string) =>
      api.delete<{ ok: true }>(
        `/api/v1/cards/${encodeURIComponent(cardType)}/${encodeURIComponent(cardId)}/tags/${encodeURIComponent(tagId)}`,
      ),
    onSuccess: () => {
      void invalidate();
      void qc.invalidateQueries({ queryKey: ['v1', 'tags'] });
      // BUG-V2-058: card list refletir badge removido.
      invalidateCardLists();
    },
  });

  return { add, remove };
}
